import React, { useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Alert, CircularProgress, Typography, IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import { generarEscritoAmparoDesdeDatos, generarEscritoAmparoTramite } from '../../services/amparoEscritoService';

function descargarBlob(data, nombreArchivo) {
    const url = window.URL.createObjectURL(new Blob([data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', nombreArchivo);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
}

/**
 * Botón "Generar Escrito de Amparo" del módulo Amparo por mora ante ANSES. Molde:
 * BotonGenerarAmparoPnc, con los mismos dos modos según qué prop llega:
 *  - `idTramite` (ficha): el trámite ya existe, se genera desde la entidad guardada y además queda
 *    adjuntado a sus Archivos.
 *  - `datos` sin `idTramite` (alta): el trámite todavía no existe, se genera solo con lo que ya se
 *    tipeó en el formulario (sin persistir nada).
 *
 * A diferencia de Amparo PNC, acá no hay que elegir plantilla: es siempre la de ANSES. El modal
 * solo confirma y avisa de los blancos que queden a completar a mano — que ahora son menos, porque
 * el renglón del tipo de trámite lo llena el subtipo.
 */
export default function BotonGenerarEscritoAmparo({ idTramite, datos, onGenerado }) {
    const [modalAbierto, setModalAbierto] = useState(false);
    const [generando, setGenerando] = useState(false);

    const esFicha = !!idTramite;
    const tieneClienteSeleccionado = esFicha || !!(datos && datos.cuil);

    if (!tieneClienteSeleccionado) {
        return (
            <button type="button" className="btn btn-outline-secondary d-flex align-items-center gap-2 rounded-pill" disabled>
                <i className="bi bi-file-earmark-richtext"></i>
                Generar Escrito de Amparo
            </button>
        );
    }

    // Lo que el escrito NO puede completar solo. El tipo de trámite ya no está en esta lista: lo
    // llena el subtipo (era el motivo de mudar la plantilla a este módulo, ver V239).
    const avisos = [];
    if (!datos?.subtipo) {
        avisos.push('Todavía no elegiste por qué trámite se ampara: el renglón del tipo de trámite va a salir en blanco (__________).');
    }
    if (!datos?.tiempoComprometido) {
        avisos.push('El "Tiempo comprometido" no está cargado: el documento lo deja en blanco (__________) y hay que completarlo a mano en Word.');
    }
    if (!datos?.nroExpedienteAnses) {
        avisos.push('No hay número de expediente de ANSES cargado: el escrito va a salir sin él.');
    }

    const handleConfirmar = async () => {
        setGenerando(true);
        try {
            const response = esFicha
                ? await generarEscritoAmparoTramite(idTramite)
                : await generarEscritoAmparoDesdeDatos(datos);

            descargarBlob(response.data, `AMPARO POR MORA - ${datos?.cuil || idTramite}.docx`);
            toast.success('Descargando el escrito de Amparo por mora');
            setModalAbierto(false);
            if (onGenerado) onGenerado();
        } catch (err) {
            console.error('❌ Error generando el escrito de Amparo por mora:', err);
            toast.error('No se pudo generar el escrito de Amparo por mora');
        } finally {
            setGenerando(false);
        }
    };

    return (
        <>
            <button
                type="button"
                className="btn btn-outline-primary d-flex align-items-center gap-2 rounded-pill"
                onClick={() => setModalAbierto(true)}
            >
                <i className="bi bi-file-earmark-richtext"></i>
                Generar Escrito de Amparo
            </button>
            <Dialog open={modalAbierto} onClose={generando ? undefined : () => setModalAbierto(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    Generar escrito de Amparo por mora
                    <IconButton type="button" onClick={() => setModalAbierto(false)} disabled={generando} size="small">
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Se va a generar: <strong>Amparo por mora ante ANSES</strong>
                    </Typography>
                    {avisos.map((aviso, i) => (
                        <Alert key={i} severity="warning" sx={{ mb: 1 }}>{aviso}</Alert>
                    ))}
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Revisá el documento generado antes de presentarlo: nombre, DNI, domicilio, N° de
                        expediente y fecha de inicio salen del cliente y del trámite.
                        {esFicha && ' El .docx queda adjuntado a los Archivos del trámite.'}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button type="button" onClick={() => setModalAbierto(false)} disabled={generando}>Cancelar</Button>
                    <Button
                        type="button"
                        variant="contained"
                        onClick={handleConfirmar}
                        disabled={generando}
                        startIcon={generando ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                        {generando ? 'Generando...' : 'Generar y descargar'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
